import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import socketService from '../services/socketService';
import APIKit from '../../config/APIKit';

// Import socket.io-client
import io from 'socket.io-client';
import { BASE_URL } from '../../config/config';

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState({}); // { conversationId: [messages] }
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [typingUsers, setTypingUsers] = useState({}); // { conversationId: [userId] }
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [socket, setSocket] = useState(null);

  // Support both old and new store structure
  const loggedUser = useSelector((state) => state.auth || state.logged || {});
  const [currentUserId, setCurrentUserId] = useState(null);

  // Fetch current user ID and setup socket connection
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        // Try to get from state first
        const userIdFromState = loggedUser?.user?.id || loggedUser?.user?.user?.id || loggedUser?.id || loggedUser?.userData?.id;
        const token = loggedUser?.token || loggedUser?.accessToken || localStorage.getItem('accessToken');

        let userId = userIdFromState;

        if (!userId && token) {
          // Fallback: fetch from API
          const res = await APIKit.get('/me');
          if (res && res.data) {
            userId = res.data.id || res.data.user?.id || res.id;
          }
        }

        if (userId && token) {
          setCurrentUserId(userId);

          // Connect to socket
          const socketUrl = BASE_URL.replace('/api', '');
          const newSocket = io(socketUrl, {
            auth: {
              token: token,
              userId: userId
            },
            transports: ['websocket', 'polling']
          });

          setSocket(newSocket);
          setupSocketListeners(newSocket, userId);
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };

    fetchCurrentUser();

    // Cleanup socket on unmount
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [loggedUser]);

  // Helper functions (defined first for use in setupSocketListeners)
  const updateConversationUnread = useCallback((conversationId, increment = true) => {
    setConversations((prev) =>
      prev.map((conv) =>
        conv.id === conversationId
          ? { ...conv, unreadCount: (conv.unreadCount || 0) + (increment ? 1 : -1) }
          : conv
      )
    );
  }, []);

  const markMessageAsRead = useCallback(async (messageId) => {
    try {
      await APIKit.put(`/chat/messages/${messageId}/read`);
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  }, []);

  const showNotification = useCallback((message) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const conversation = conversations.find((c) => c.id === message.conversationId);
      const senderName = conversation?.otherUser?.firstName || 'Someone';
      
      new Notification(`${senderName} sent a message`, {
        body: message.content,
        icon: conversation?.otherUser?.avatar || '/default-avatar.jpg',
        tag: `chat-${message.conversationId}`
      });
    }
  }, [conversations]);

  // Setup socket event listeners
  const setupSocketListeners = useCallback((socketInstance, userId) => {
    if (!socketInstance) return;

    // Connection events
    socketInstance.on('connect', () => {
      console.log('Socket connected:', socketInstance.id);
      setSocketConnected(true);
    });

    socketInstance.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      setSocketConnected(false);
    });

    socketInstance.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setSocketConnected(false);
    });

    // Receive new message
    socketInstance.on('receive-message', (data) => {
      const { conversationId, message } = data;

      setMessages((prev) => ({
        ...prev,
        [conversationId]: [...(prev[conversationId] || []), message]
      }));

      // Update conversation last message
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId
            ? { ...conv, lastMessage: message, updatedAt: message.createdAt }
            : conv
        )
      );

      // Update unread count if not active conversation
      if (activeConversation?.id !== conversationId) {
        setUnreadCount((prev) => prev + 1);
        updateConversationUnread(conversationId, true);
      } else {
        // Mark as read if active
        markMessageAsRead(message.id);
      }

      // Show notification
      showNotification(message);
    });

    // User online
    socketInstance.on('user-online', (data) => {
      setOnlineUsers((prev) => new Set([...prev, data.userId]));
    });

    // User offline
    socketInstance.on('user-offline', (data) => {
      setOnlineUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(data.userId);
        return newSet;
      });
    });

    // Typing indicator
    socketInstance.on('typing', (data) => {
      const { conversationId, userId: typingUserId } = data;
      if (typingUserId !== userId) {
        setTypingUsers((prev) => ({
          ...prev,
          [conversationId]: [...(prev[conversationId] || []), typingUserId]
        }));

        // Auto remove typing after 3 seconds
        setTimeout(() => {
          setTypingUsers((prev) => ({
            ...prev,
            [conversationId]: (prev[conversationId] || []).filter((id) => id !== typingUserId)
          }));
        }, 3000);
      }
    });

    // Stop typing
    socketInstance.on('stop-typing', (data) => {
      const { conversationId, userId: typingUserId } = data;
      setTypingUsers((prev) => ({
        ...prev,
        [conversationId]: (prev[conversationId] || []).filter((id) => id !== typingUserId)
      }));
    });

    // Message sent confirmation
    socketInstance.on('message-sent', (data) => {
      console.log('Message sent successfully:', data);
    });

    // Message error
    socketInstance.on('message-error', (data) => {
      console.error('Message send error:', data.error);
    });

  }, [activeConversation]);

  // Load conversations
  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      const res = await APIKit.get('/chat/conversations');
      if (res.status) {
        setConversations(res.data || []);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load messages for a conversation
  const loadMessages = useCallback(async (conversationId) => {
    try {
      const res = await APIKit.get(`/chat/messages/${conversationId}`);
      if (res.status) {
        setMessages((prev) => ({
          ...prev,
          [conversationId]: res.data || []
        }));

        // Join conversation room for real-time updates
        if (socket && socketConnected) {
          socket.emit('join-conversation', conversationId);
        }
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  }, [socket, socketConnected]);

  // Send message
  const sendMessage = useCallback(async (conversationId, content, recipientId) => {
    try {
      const messageData = {
        conversationId,
        content,
        recipientId,
        senderId: currentUserId
      };

      // Send via socket for real-time
      if (socket && socketConnected) {
        socket.emit('send-message', messageData);

        // Join conversation room if not already joined
        socket.emit('join-conversation', conversationId);
      }

      // Also save via API for persistence
      const res = await APIKit.post('/chat/messages', messageData);
      if (res.status && res.data) {
        const newMessage = res.data;

        setMessages((prev) => ({
          ...prev,
          [conversationId]: [...(prev[conversationId] || []), newMessage]
        }));

        // Update conversation
        setConversations((prev) =>
          prev.map((conv) =>
            conv.id === conversationId
              ? { ...conv, lastMessage: newMessage, updatedAt: newMessage.createdAt }
              : conv
          )
        );

        return newMessage;
      }
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }, [currentUserId, socket, socketConnected]);

  // Create or get conversation
  const getOrCreateConversation = useCallback(async (otherUserId) => {
    try {
      const res = await APIKit.post('/chat/conversations', {
        userId: otherUserId
      });
      
      if (res.status && res.data) {
        const conversation = res.data;
        
        // Add to conversations if not exists
        setConversations((prev) => {
          const exists = prev.find((c) => c.id === conversation.id);
          if (!exists) {
            return [...prev, conversation];
          }
          return prev;
        });

        return conversation;
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  }, []);

  // Mark conversation as read
  const markConversationAsRead = useCallback(async (conversationId) => {
    try {
      await APIKit.put(`/chat/conversations/${conversationId}/read`);
      
      // Update local state
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv
        )
      );

      // Update unread count
      const conversation = conversations.find((c) => c.id === conversationId);
      if (conversation?.unreadCount) {
        setUnreadCount((prev) => Math.max(0, prev - conversation.unreadCount));
      }
    } catch (error) {
      console.error('Error marking conversation as read:', error);
    }
  }, [conversations]);

  // Typing indicator
  const sendTyping = useCallback((conversationId, isTyping) => {
    if (!conversationId || !socket || !socketConnected) return;

    socket.emit(isTyping ? 'typing' : 'stop-typing', {
      conversationId,
      userId: currentUserId
    });
  }, [currentUserId, socket, socketConnected]);

  // Request notification permission
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  }, []);

  // Load unread count
  const loadUnreadCount = useCallback(async () => {
    try {
      const res = await APIKit.get('/chat/unread-count');
      if (res.status) {
        setUnreadCount(res.data?.count || 0);
      }
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    if (currentUserId) {
      loadConversations();
      loadUnreadCount();
      requestNotificationPermission();
    }
  }, [currentUserId, loadConversations, loadUnreadCount, requestNotificationPermission]);

  // Mark conversation as read when active
  useEffect(() => {
    if (activeConversation) {
      markConversationAsRead(activeConversation.id);
      loadMessages(activeConversation.id);
    }
  }, [activeConversation, markConversationAsRead, loadMessages]);

  const value = {
    conversations,
    activeConversation,
    setActiveConversation,
    messages: messages[activeConversation?.id] || [],
    onlineUsers,
    typingUsers: typingUsers[activeConversation?.id] || [],
    unreadCount,
    loading,
    socketConnected,
    sendMessage,
    getOrCreateConversation,
    loadConversations,
    loadMessages,
    markMessageAsRead,
    markConversationAsRead,
    sendTyping,
    requestNotificationPermission,
    currentUserId
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

